import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Ban,
  CheckCircle2,
  Search,
  Loader2,
  Filter,
  UserPlus,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Save
} from 'lucide-react';
import { ApiServices } from '../../services/ApiServices';

// shadcn components
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";

/**
 * UserManagement component
 * Manages users with role and grade-based permissions.
 * Refactored to shadcn/ui with black/white theme.
 */
const UserManagement = () => {
  const [permissions, setPermissions] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [roleFilter, setRoleFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: '',
    grade: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Load permissions and users
  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (permissions?.can_view_users) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, roleFilter, gradeFilter, searchQuery, permissions]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 2500);
    return () => clearTimeout(t);
  }, [successMessage]);

  const loadPermissions = async () => {
    try {
      const response = await ApiServices.getUserPermissions();
      if (response.success) {
        setPermissions(response.permissions);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Не удалось загрузить права доступа');
    }
  };

  const loadUsers = async ({ pageOverride } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pageOverride ?? page,
        page_size: pageSize,
      };
      if (roleFilter && roleFilter !== 'all') params.role = roleFilter;
      if (gradeFilter && gradeFilter !== 'all') params.grade = gradeFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await ApiServices.getUsers(params);
      if (response.success) {
        setUsers(response.users);
        setTotalCount(response.count);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: permissions?.allowed_roles[0] || '',
      grade: permissions?.allowed_grades[0] || '',
    });
    setFormErrors({});
    setCreateDialogOpen(true);
  };

  const handleEditUser = async (user) => {
    try {
      const response = await ApiServices.getUserById(user.id);
      if (response.success) {
        setSelectedUser(response.user);
        setFormData({
          username: response.user.username,
          password: '',
          confirmPassword: '',
          role: response.user.role,
          grade: response.user.grade || '',
        });
        setFormErrors({});
        setEditDialogOpen(true);
      }
    } catch (err) {
      setError(err.message || 'Не удалось загрузить данные пользователя');
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) errors.username = 'Имя пользователя обязательно';
    if (createDialogOpen && !formData.password) errors.password = 'Пароль обязателен';
    if (formData.password && formData.password.length < 6) errors.password = 'Пароль должен быть не менее 6 символов';
    if (formData.password && formData.password !== formData.confirmPassword) errors.confirmPassword = 'Пароли не совпадают';
    if (!formData.role) errors.role = 'Роль обязательна';
    if (formData.role !== 'admin' && !formData.grade) errors.grade = 'Уровень обязателен';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const userData = {
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role,
        grade: formData.role === 'admin' ? null : formData.grade,
      };

      const response = await ApiServices.createUser(userData);
      if (response.success) {
        setCreateDialogOpen(false);
        setSuccessMessage(`Пользователь "${userData.username}" создан`);
        // New users are ordered by created_at desc; jump to page 1 so it's visible.
        if (page !== 1) setPage(1);
        await loadUsers({ pageOverride: 1 });
      }
    } catch (err) {
      setFormErrors({ submit: err.message || 'Ошибка при создании пользователя' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const userData = {};
      if (formData.username !== selectedUser.username) userData.username = formData.username.trim();
      if (formData.password) userData.password = formData.password;
      if (permissions?.can_delete_users) {
        if (formData.role !== selectedUser.role) userData.role = formData.role;
        if (formData.grade !== selectedUser.grade) userData.grade = formData.role === 'admin' ? null : formData.grade;
      }

      const response = await ApiServices.updateUser(selectedUser.id, userData);
      if (response.success) {
        setEditDialogOpen(false);
        setSuccessMessage(`Пользователь "${formData.username}" обновлён`);
        await loadUsers();
      }
    } catch (err) {
      setFormErrors({ submit: err.message || 'Ошибка при обновлении пользователя' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const response = await ApiServices.deleteUser(selectedUser.id);
      if (response.success) {
        setDeleteDialogOpen(false);
        setSuccessMessage(`Пользователь "${selectedUser.username}" удалён`);
        await loadUsers();
      }
    } catch (err) {
      setError(err.message || 'Ошибка при удалении пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (user) => {
    setLoading(true);
    try {
      const response = await ApiServices.deactivateUser(user.id);
      if (response.success) {
        setSuccessMessage(`Пользователь "${user.username}" деактивирован`);
        await loadUsers();
      }
    } catch (err) {
      setError(err.message || 'Ошибка при деактивации пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (user) => {
    setLoading(true);
    try {
      const response = await ApiServices.activateUser(user.id);
      if (response.success) {
        setSuccessMessage(`Пользователь "${user.username}" активирован`);
        await loadUsers();
      }
    } catch (err) {
      setError(err.message || 'Ошибка при активации пользователя');
    } finally {
      setLoading(false);
    }
  };

  if (permissions && !permissions.can_view_users) return null;
  if (!permissions) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
      </div>
    );
  }

  return (
    <Card>
      {/* Header section */}
      <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle>Управление пользователями</CardTitle>
          <CardDescription>Управление пользователями системы и их уровнями доступа.</CardDescription>
        </div>
        {permissions.can_create_users && (
          <Button onClick={handleCreateUser} className="font-semibold shrink-0">
            <UserPlus className="mr-2 h-4 w-4" />
            Создать пользователя
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
        <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-1">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Ошибка</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="animate-in fade-in slide-in-from-top-1">
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Успех</AlertTitle>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-70" />
            <Input
              placeholder="Поиск по имени пользователя..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>

          {permissions.can_delete_users && (
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Роль:</span>
                <Select value={roleFilter} onValueChange={(val) => { setRoleFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-[120px] h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ops">Ops</SelectItem>
                    <SelectItem value="mb">MB</SelectItem>
                    <SelectItem value="promo">Promo</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Грейд:</span>
                <Select value={gradeFilter} onValueChange={(val) => { setGradeFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="rounded-md border overflow-hidden">
          <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="font-semibold text-foreground">Timestamp</TableHead>
              <TableHead className="font-semibold text-foreground">Username</TableHead>
              <TableHead className="font-semibold text-foreground">Role</TableHead>
              <TableHead className="font-semibold text-foreground">Grade</TableHead>
              <TableHead className="font-semibold text-foreground">Status</TableHead>
              <TableHead className="font-semibold text-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto opacity-30" />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground italic">
                  No users found matching your filters.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} className="group transition-colors hover:bg-muted/10">
                  <TableCell className="text-muted-foreground text-sm">
                    {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="font-medium text-foreground">{user.username}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 hover:bg-primary/10 transition-colors uppercase font-bold text-[10px]">
                      {user.role || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-[10px] uppercase font-semibold">
                      {user.grade || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={user.is_active ? "default" : "destructive"}
                      className={cn(
                        "text-[10px] uppercase font-bold px-2 py-0 border",
                        user.is_active ? "bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20" : "bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/20"
                      )}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {permissions.can_edit_users && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditUser(user)}
                          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          title="Edit User"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}

                      {permissions.can_deactivate_users && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => (user.is_active ? handleDeactivate(user) : handleActivate(user))}
                          className={cn(
                            "h-8 w-8 transition-colors",
                            user.is_active ? "text-muted-foreground hover:text-destructive hover:bg-destructive/10" : "text-muted-foreground hover:text-green-600 hover:bg-green-600/10"
                          )}
                          title={user.is_active ? "Deactivate User" : "Activate User"}
                        >
                          {user.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </Button>
                      )}

                      {permissions.can_delete_users && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border-l rounded-none"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>

        {totalCount > pageSize && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className={cn("cursor-pointer", page === 1 && "pointer-events-none opacity-50")}
              />
            </PaginationItem>

            {Array.from({ length: Math.ceil(totalCount / pageSize) }).map((_, i) => {
              const p = i + 1;
              if (p === 1 || p === Math.ceil(totalCount / pageSize) || (p >= page - 1 && p <= page + 1)) {
                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={page === p}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
              if (p === page - 2 || p === page + 2) {
                return <PaginationItem key={p}><PaginationEllipsis /></PaginationItem>;
              }
              return null;
            })}

            <PaginationItem>
              <PaginationNext
                onClick={() => setPage(p => Math.min(Math.ceil(totalCount / pageSize), p + 1))}
                className={cn("cursor-pointer", page === Math.ceil(totalCount / pageSize) && "pointer-events-none opacity-50")}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      </CardContent>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-popover border-border">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Создать нового пользователя</DialogTitle>
            <DialogDescription>Добавление нового пользователя в систему с определенными ролями и правами доступа.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 text-foreground">
            {formErrors.submit && (
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>{formErrors.submit}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="create-username">Имя пользователя <span className="text-destructive">*</span></Label>
              <Input
                id="create-username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={cn(formErrors.username && "border-destructive")}
                placeholder="Введите имя пользователя"
              />
              {formErrors.username && <p className="text-xs text-destructive font-medium">{formErrors.username}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="create-password">Пароль <span className="text-destructive">*</span></Label>
                <Input
                  id="create-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={cn(formErrors.password && "border-destructive")}
                  placeholder="••••••••"
                />
                {formErrors.password && <p className="text-xs text-destructive font-medium">{formErrors.password}</p>}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="create-confirm">Подтвердите пароль <span className="text-destructive">*</span></Label>
                <Input
                  id="create-confirm"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={cn(formErrors.confirmPassword && "border-destructive")}
                  placeholder="••••••••"
                />
                {formErrors.confirmPassword && <p className="text-xs text-destructive font-medium">{formErrors.confirmPassword}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="grid gap-2">
                <Label>Роль <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val, grade: val === 'admin' ? '' : formData.grade })}
                >
                  <SelectTrigger className={cn(formErrors.role && "border-destructive")}>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {permissions.allowed_roles.map((role) => (
                      <SelectItem key={role} value={role}>{role.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.role && <p className="text-xs text-destructive font-medium">{formErrors.role}</p>}
              </div>

              {formData.role && formData.role !== 'admin' && (
                <div className="grid gap-2">
                  <Label>Grade <span className="text-destructive">*</span></Label>
                  <Select
                    value={formData.grade}
                    onValueChange={(val) => setFormData({ ...formData, grade: val })}
                  >
                    <SelectTrigger className={cn(formErrors.grade && "border-destructive")}>
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {permissions.allowed_grades.map((grade) => (
                        <SelectItem key={grade} value={grade}>{grade.charAt(0).toUpperCase() + grade.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.grade && <p className="text-xs text-destructive font-medium">{formErrors.grade}</p>}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleSubmitCreate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Создать пользователя
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-popover border-border overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Редактирование: {selectedUser?.username}</DialogTitle>
            <DialogDescription>Обновите данные пользователя или сбросьте пароль.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 text-foreground">
            {formErrors.submit && (
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>{formErrors.submit}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="edit-username">Имя пользователя</Label>
              <Input
                id="edit-username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={cn(formErrors.username && "border-destructive")}
              />
              {formErrors.username && <p className="text-xs text-destructive font-medium">{formErrors.username}</p>}
            </div>

            <Separator className="my-2" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Смена пароля (необязательно)</p>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-password">Новый пароль</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-confirm">Подтвердите пароль</Label>
                <Input
                  id="edit-confirm"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {permissions.can_delete_users && (
              <>
                <Separator className="my-2" />
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="grid gap-2">
                    <Label>Роль</Label>
                    <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val, grade: val === 'admin' ? '' : formData.grade })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ops">OPS</SelectItem>
                        <SelectItem value="mb">MB</SelectItem>
                        <SelectItem value="promo">PROMO</SelectItem>
                        <SelectItem value="admin">ADMIN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.role !== 'admin' && (
                    <div className="grid gap-2">
                      <Label>Грейд</Label>
                      <Select value={formData.grade} onValueChange={(val) => setFormData({ ...formData, grade: val })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="employee">Employee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleSubmitEdit} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Сохранить изменения
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-popover border-destructive/20 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete user <span className="font-bold text-foreground">&quot;{selectedUser?.username}&quot;</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="bg-muted/30 px-6 py-4 -mx-6 -mb-6 mt-4">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserManagement;

